import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { useLang } from "../context/LanguageContext";
import { CODE_TO_URL } from "../lib/i18nRouter";
import { Sparkles, Gift, Calendar, Stars } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";
import { breadcrumbSchema } from "../lib/schema";
import GemologistAssistant from "../components/GemologistAssistant";

const ZODIACS_PREVIEW = [
  "aries","taurus","gemini","cancer","leo","virgo",
  "libra","scorpio","sagittarius","capricorn","aquarius","pisces",
];

const FindYourStone = () => {
  const { format } = useCurrency();
  const [birthstones, setBirthstones] = useState([]);
  const [zodiacs, setZodiacs] = useState([]);
  const [showMatch, setShowMatch] = useState(false);
  const [showGift, setShowGift] = useState(false);

  useEffect(() => {
    api.get("/birthstones").then(({ data }) => setBirthstones(data));
    api.get("/zodiacs").then(({ data }) => setZodiacs(data));
  }, []);

  return (
    <div data-testid="fys-page" className="pt-32">
      <Seo
        title="Find Your Stone · Birthstones, Zodiac & AI Gemstone Match"
        description="Discover the gemstone connected to your destiny. Explore the 12 birthstones, 12 zodiac gemstones, and let our AI gemologist-advisor recommend a stone aligned with your purpose."
        path="/find-your-stone"
        jsonLd={breadcrumbSchema([
          { name: "Home", url: "/" },
          { name: "Find Your Stone", url: "/find-your-stone" },
        ])}
      />
      {/* HERO */}
      <section className="relative h-[80vh] overflow-hidden grain mb-32">
        <img src="https://images.unsplash.com/photo-1735480166632-38176b26f212" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black"></div>
        <div className="relative z-10 max-w-[1600px] mx-auto h-full px-6 md:px-12 lg:px-16 flex flex-col justify-end pb-20">
          <div className="reveal-up">
            <p className="font-accent text-[11px] md:text-sm tracking-[0.4em] uppercase text-gold mb-6">Find Your Stone</p>
            <h1 className="font-heading font-light tracking-tight text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] max-w-5xl">
              Discover the gemstone<br />
              <span className="italic text-ivory">connected to your destiny.</span>
            </h1>
            <p className="mt-8 max-w-xl text-white/70 text-base md:text-lg font-light leading-relaxed">
              An interactive consultation with our gemologist-advisor. Begin with the month of your birth, your zodiac sign, or let our AI advisor recommend a stone aligned with your purpose.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <button data-testid="cta-match" onClick={() => setShowMatch(true)} className="bg-gold text-black font-accent uppercase tracking-[0.28em] text-xs px-9 py-4 hover:bg-ivory transition-colors duration-500 flex items-center gap-2">
                <Sparkles size={14} /> Smart Match
              </button>
              <button data-testid="cta-gift" onClick={() => setShowGift(true)} className="border border-white/30 text-white font-accent uppercase tracking-[0.28em] text-xs px-9 py-4 hover:border-gold hover:text-gold transition-colors duration-500 flex items-center gap-2">
                <Gift size={14} /> Find Perfect Gift Stone
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* BIRTHSTONES */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 mb-32">
        <div className="flex items-center gap-3 mb-5">
          <Calendar size={14} className="text-gold" />
          <p className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold">By Birth Month</p>
        </div>
        <h2 className="font-heading font-light text-4xl md:text-6xl leading-tight max-w-3xl mb-16">Twelve months. Twelve stones. Each a portrait.</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {birthstones.map((b) => (
            <Link key={b.slug} to={`/birthstone/${b.slug}`} data-testid={`birth-card-${b.slug}`} className="group block bg-onyx border border-white/5 hover:border-gold/40 transition-all duration-700">
              <div className="aspect-[4/5] overflow-hidden bg-black">
                <img src={b.image_url} alt={b.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" loading="lazy" />
              </div>
              <div className="px-5 py-5 border-t border-white/5">
                <p className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold/80 mb-1">
                  {new Date(2024, b.month - 1, 1).toLocaleString("en", { month: "long" })}
                </p>
                <h3 className="font-heading text-2xl leading-tight">{b.name}</h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ZODIACS */}
      <section className="bg-onyx grain relative py-32 mb-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex items-center gap-3 mb-5">
            <Stars size={14} className="text-gold" />
            <p className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold">By Zodiac Sign</p>
          </div>
          <h2 className="font-heading font-light text-4xl md:text-6xl leading-tight max-w-3xl mb-16">A stone for every signature in the sky.</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {zodiacs.map((z) => (
              <Link key={z.slug} to={`/zodiac/${z.slug}`} data-testid={`zodiac-card-${z.slug}`} className="group block bg-noir border border-white/10 hover:border-gold/40 transition-all duration-700 px-5 py-6 text-center">
                <p className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold/70 mb-2">{z.element}</p>
                <h3 className="font-heading text-2xl leading-tight">{z.name}</h3>
                <p className="font-accent text-[9px] tracking-[0.18em] uppercase text-white/40 mt-2">{z.dates}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {showMatch && <MatchModal onClose={() => setShowMatch(false)} format={format} />}
      {showGift && <GiftModal onClose={() => setShowGift(false)} format={format} />}
      {/* AI Gemologist — astrology-aware floating assistant */}
      <GemologistAssistant />
    </div>
  );
};

const MatchModal = ({ onClose, format }) => {
  const [form, setForm] = useState({ birthday: "", zodiac: "", purpose: "self", budget_max: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/match", {
        birthday: form.birthday || null,
        zodiac: form.zodiac || null,
        purpose: form.purpose,
        budget_max: form.budget_max ? Number(form.budget_max) : null,
      });
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Match failed");
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title="Smart Match" eyebrow="AI Gemologist-Advisor" testid="match-modal">
      {!result ? (
        <form onSubmit={submit} className="space-y-6">
          <Field label="Birthday" testid="match-bday" type="date" value={form.birthday} onChange={(v) => setForm({...form, birthday: v})} />
          <SelectField label="Zodiac (optional)" testid="match-zodiac" value={form.zodiac} onChange={(v) => setForm({...form, zodiac: v})}
            options={[{ v: "", l: "—" }, ...ZODIACS_PREVIEW.map((z) => ({ v: z, l: z[0].toUpperCase() + z.slice(1) }))]} />
          <SelectField label="Purpose" testid="match-purpose" value={form.purpose} onChange={(v) => setForm({...form, purpose: v})}
            options={[
              { v: "self", l: "For myself" }, { v: "love", l: "Love" }, { v: "wealth", l: "Wealth" },
              { v: "protection", l: "Protection" }, { v: "investment", l: "Investment" }, { v: "gift", l: "Gift" },
            ]} />
          <Field label="Budget cap (USD)" testid="match-budget" type="number" value={form.budget_max} onChange={(v) => setForm({...form, budget_max: v})} placeholder="e.g. 500000" />
          <button data-testid="match-submit" disabled={busy} className="w-full bg-gold text-black font-accent uppercase tracking-[0.28em] text-xs py-4 hover:bg-ivory disabled:opacity-50">
            {busy ? "Consulting…" : "Reveal My Stone"}
          </button>
        </form>
      ) : (
        <MatchResult result={result} format={format} onReset={() => setResult(null)} />
      )}
    </Modal>
  );
};

const GiftModal = ({ onClose, format }) => {
  const [form, setForm] = useState({ relationship: "", occasion: "", recipient_birthday: "", recipient_zodiac: "", budget_max: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/gift-match", {
        relationship: form.relationship, occasion: form.occasion,
        recipient_birthday: form.recipient_birthday || null,
        recipient_zodiac: form.recipient_zodiac || null,
        budget_max: Number(form.budget_max),
      });
      setResult(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title="Perfect Gift Stone" eyebrow="Gift Curation" testid="gift-modal">
      {!result ? (
        <form onSubmit={submit} className="space-y-6">
          <Field label="Relationship" testid="gift-rel" required value={form.relationship} onChange={(v) => setForm({...form, relationship: v})} placeholder="e.g. wife, partner, mother" />
          <Field label="Occasion" testid="gift-occ" required value={form.occasion} onChange={(v) => setForm({...form, occasion: v})} placeholder="e.g. anniversary, milestone birthday" />
          <Field label="Recipient birthday (optional)" testid="gift-bday" type="date" value={form.recipient_birthday} onChange={(v) => setForm({...form, recipient_birthday: v})} />
          <SelectField label="Recipient zodiac (optional)" testid="gift-zodiac" value={form.recipient_zodiac} onChange={(v) => setForm({...form, recipient_zodiac: v})}
            options={[{ v: "", l: "—" }, ...ZODIACS_PREVIEW.map((z) => ({ v: z, l: z[0].toUpperCase() + z.slice(1) }))]} />
          <Field label="Budget cap (USD)" testid="gift-budget" type="number" required value={form.budget_max} onChange={(v) => setForm({...form, budget_max: v})} placeholder="e.g. 50000" />
          <button data-testid="gift-submit" disabled={busy} className="w-full bg-gold text-black font-accent uppercase tracking-[0.28em] text-xs py-4 hover:bg-ivory disabled:opacity-50">
            {busy ? "Curating…" : "Suggest a Gift"}
          </button>
        </form>
      ) : (
        <GiftResult result={result} format={format} onReset={() => setResult(null)} />
      )}
    </Modal>
  );
};

const Modal = ({ onClose, title, eyebrow, testid, children }) => (
  <div data-testid={testid} className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
    <div className="max-w-2xl mx-auto my-16 bg-noir border border-gold/30 p-8 md:p-10" onClick={(e) => e.stopPropagation()}>
      <p className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-3">{eyebrow}</p>
      <h2 className="font-heading text-4xl mb-8">{title}</h2>
      {children}
      <button onClick={onClose} data-testid={`${testid}-close`} className="mt-8 text-white/40 font-accent text-[10px] tracking-[0.28em] uppercase hover:text-white">Close</button>
    </div>
  </div>
);

const Field = ({ label, testid, type = "text", value, onChange, required, placeholder }) => (
  <div>
    <label className="font-accent text-[10px] tracking-[0.24em] uppercase text-white/40 block mb-2">{label}</label>
    <input data-testid={testid} type={type} required={required} value={value} placeholder={placeholder}
           onChange={(e) => onChange(e.target.value)}
           className="bg-transparent border-b border-white/20 px-0 py-3 w-full focus:outline-none focus:border-gold text-white" />
  </div>
);

const SelectField = ({ label, testid, value, onChange, options }) => (
  <div>
    <label className="font-accent text-[10px] tracking-[0.24em] uppercase text-white/40 block mb-2">{label}</label>
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
            className="bg-transparent border-b border-white/20 px-0 py-3 w-full focus:outline-none focus:border-gold text-white">
      {options.map((o) => <option key={o.v} value={o.v} className="bg-noir">{o.l}</option>)}
    </select>
  </div>
);

const MatchResult = ({ result, format, onReset }) => {
  const ctx = result.context || {};
  const { lang } = useLang();
  const slug = CODE_TO_URL[lang] || "en";
  const [downloading, setDownloading] = useState(false);

  const downloadPassport = async () => {
    if (!result.primary) return;
    setDownloading(true);
    try {
      const resp = await api.post(
        "/gem-passport",
        {
          primary_id: result.primary.id,
          secondary_ids: (result.secondary || []).map((s) => s.id),
          premium_collector_id: result.premium_collector?.id || null,
          narrative: result.narrative,
          birthstone_slug: ctx.birthstone?.slug || null,
          zodiac_slug: ctx.zodiac?.slug || null,
          purpose: ctx.purpose || null,
        },
        { responseType: "blob" }
      );
      const code = resp.headers["x-claim-code"] || resp.headers["X-Claim-Code"] || "passport";
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `opikjewels-gem-passport-${code}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Passport ready · claim code ${code}`);
    } catch (err) {
      toast.error("Could not generate passport");
    } finally { setDownloading(false); }
  };
  const renderStone = (gem, label) => gem && (
    <Link to={`/${slug}/gemstone/${gem.id}`} className="block bg-onyx border border-white/10 hover:border-gold/40 transition-all duration-500 group">
      <div className="aspect-[4/3] overflow-hidden">
        <img src={gem.image_url} alt={gem.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" />
      </div>
      <div className="p-5">
        <p className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-2">{label}</p>
        <div className="font-heading text-xl">{gem.name}</div>
        <div className="text-white/50 text-xs mt-1">{gem.carat}ct {gem.category} · {gem.origin}</div>
        <div className="text-ivory font-heading text-lg mt-2">{format(gem.reserve_price, gem.currency)}</div>
        {result.why_it_fits?.[gem.id] && <p className="text-white/60 text-sm mt-3 leading-relaxed">{result.why_it_fits[gem.id]}</p>}
      </div>
    </Link>
  );

  return (
    <div data-testid="match-result">
      {(ctx.birthstone || ctx.zodiac) && (
        <div className="mb-6 text-white/50 text-sm font-accent tracking-[0.18em] uppercase">
          {ctx.birthstone && <span>{ctx.birthstone.name}</span>}
          {ctx.birthstone && ctx.zodiac && <span> · </span>}
          {ctx.zodiac && <span>{ctx.zodiac.name}</span>}
        </div>
      )}
      <p className="text-white/85 leading-relaxed mb-8 font-light">{result.narrative}</p>
      <div className="grid grid-cols-1 gap-4 mb-6">
        {renderStone(result.primary, "Primary")}
      </div>
      {result.secondary?.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {result.secondary.map((g, i) => <div key={g.id}>{renderStone(g, `Secondary · ${i + 1}`)}</div>)}
        </div>
      )}
      {result.premium_collector && <div className="mb-6">{renderStone(result.premium_collector, "Premium Collector")}</div>}
      <button
        data-testid="passport-download"
        onClick={downloadPassport}
        disabled={downloading || !result.primary}
        className="w-full bg-gold text-black font-accent uppercase tracking-[0.28em] text-xs py-4 hover:bg-ivory transition-colors duration-500 disabled:opacity-50 mb-3"
      >
        {downloading ? "Drafting your passport…" : "Download Personal Gem Passport (PDF)"}
      </button>
      <a href="https://wa.me/6287781058814" target="_blank" rel="noreferrer" data-testid="match-whatsapp"
         className="block text-center border border-gold text-gold font-accent uppercase tracking-[0.28em] text-xs py-4 hover:bg-gold hover:text-black mb-3">
        Discuss with Concierge on WhatsApp
      </a>
      <button onClick={onReset} className="text-white/40 font-accent text-[10px] tracking-[0.28em] uppercase hover:text-white">Refine criteria</button>
    </div>
  );
};

const GiftResult = ({ result, format, onReset }) => {
  const { lang } = useLang();
  const slug = CODE_TO_URL[lang] || "en";
  const renderStone = (gem, label) => gem && (
    <Link to={`/${slug}/gemstone/${gem.id}`} className="block bg-onyx border border-white/10 hover:border-gold/40 transition-all duration-500 group">
      <div className="aspect-[4/3] overflow-hidden"><img src={gem.image_url} alt={gem.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" /></div>
      <div className="p-5">
        <p className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-2">{label}</p>
        <div className="font-heading text-xl">{gem.name}</div>
        <div className="text-white/50 text-xs mt-1">{gem.carat}ct {gem.category} · {gem.origin}</div>
        <div className="text-ivory font-heading text-lg mt-2">{format(gem.reserve_price, gem.currency)}</div>
        {result.why_it_fits?.[gem.id] && <p className="text-white/60 text-sm mt-3 leading-relaxed">{result.why_it_fits[gem.id]}</p>}
      </div>
    </Link>
  );
  return (
    <div data-testid="gift-result">
      <p className="text-white/85 leading-relaxed mb-8 font-light">{result.narrative}</p>
      <div className="grid grid-cols-1 gap-4 mb-3">{renderStone(result.primary, "Primary Suggestion")}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        {renderStone(result.safe, "Safe Choice")}
        {renderStone(result.bold, "Bold Choice")}
      </div>
      <button onClick={onReset} className="text-white/40 font-accent text-[10px] tracking-[0.28em] uppercase hover:text-white">Refine criteria</button>
    </div>
  );
};

export default FindYourStone;
